import Enumerable from "linq";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { ITagValue } from "../../common/models/ITagValue";
import { RouteFromObjectType } from "../../common/utilities/RouteFromObjectType";
import { DynaCardTypeEnum } from "../dynacard-data-service/Models";
import { ITagRegisterInfo } from "../properties-data-service/Models";
import restService from "../rest-service/RestService";
import storageService from "../storage/storage-service/StorageService";
import { StorageKeys } from "../storage/StorageKeys";
import { ILiveDataStatusInfoModel, ILiveDataStatusModel } from "./Models";

//type ObjectSearchQueryParams = {
//    searchString: string
//}


export const STATUS_NOTIFICATION_TIMEOUT = 180000;    // 3 minutes in milliseconds
export const STATUS_POLLING_INTERVAL = 10000;


type DemandScanQueryParms = {
    emailAddress: string | undefined,
    writeOnly: boolean | undefined
};

type WriteDownQueryParams = {
    emailAddress: string | undefined,
    delayedWrite: boolean | undefined
};

type StatusQueryParams = {
    contextGroupID: number
};

type PollingStatisticsQueryParams = {
    objectID: number;
    pollingServer: string;
    acmObjectName: string;
}

function getStatusStorageKey(deviceID: number): string {

    //let currentUserState = storageService.local.get<UserState>(getkey(), defaultUserState);
    const customerID = storageService.session.get<number>(StorageKeys.LastCustomerID, 0);
    return `livedata:${customerID}:${deviceID}`;
}

function removeStatusInfo(deviceID: number, statusID: string, infoList: ILiveDataStatusInfoModel[]): void {

    const key = getStatusStorageKey(deviceID);

    const info = infoList.find(o => o.statusID === statusID);

    if (info) {
        const ndx: number = infoList.indexOf(info);
        if (ndx !== -1) {
            infoList.splice(ndx, 1);
            if (infoList.length === 0) {
                storageService.local.remove(key)
            } else {
                storageService.local.set<ILiveDataStatusInfoModel[]>(key, infoList);
            }
        }
    }
}

function getStoredStatusInfo (deviceID: number): ILiveDataStatusInfoModel[] {

    const now: Date = new Date();

    let info = storageService.local.get<ILiveDataStatusInfoModel[]>(getStatusStorageKey(deviceID), [])
    Enumerable.from(info)
        .where(o => new Date(o.expiresOn.toString()) < now)
        .forEach((item) => removeStatusInfo(deviceID, item.statusID, info));


    info = Enumerable.from(info).where(o => new Date(o.expiresOn.toString()) > now).toArray();

    return info;
}


const LiveDataService = {

    getStoredStatusInfo: function (deviceID: number): ILiveDataStatusInfoModel[] {
        return getStoredStatusInfo(deviceID);
    },

    storeStatusInfo: function (deviceID: number, status: ILiveDataStatusModel): void {

        const key: string = getStatusStorageKey(deviceID);
        
        const infoList = getStoredStatusInfo(deviceID);
        if (!Enumerable.from(infoList).any(o => o.statusID === status.requestID)) {
            const expireDate: Date = new Date();
            expireDate.setDate(expireDate.getDate() + 1);

            const info: ILiveDataStatusInfoModel = {
                statusID: status.requestID,
                expiresOn: expireDate
            };

            infoList.push(info);
            storageService.local.set<ILiveDataStatusInfoModel[]>(key, infoList);
        }
    },

    removeStatusInfo: function(deviceID: number, status: ILiveDataStatusModel | string): void {

        const infoList = getStoredStatusInfo(deviceID);
        let statusID: string;

        if (typeof status === "string") {
            statusID = status as string;
        } else {
            statusID = (status as ILiveDataStatusModel).requestID;
        }

        removeStatusInfo(deviceID, statusID, infoList);
    },

    cancelLiveData: async function(contextGroupID: number, eLynxObject: IeLynxObject, signal?: AbortSignal): Promise<void>  {
        // var path: string = [
        //     "groups",
        //     contextGroupID,
        //     getOb
        //     eLynxObject.objectType === eLynxObjectTypeEnum.Well ?
        //         "wells" : eLynxObject.objectType === Enums.eLynxObjectTypeEnum.Device ?
        //             "devices" : "tanks",
        //     eLynxObject.id,
        //     "cancelCommand"
        // ].join("/");

        // return this._restService.put(path, "").then((data: Services.IRestResponse<any>) => {
        //     return data.entity;
        // });

        const path =`/groups/${contextGroupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/cancelCommand`;        
        await restService.put(path, signal);
    },

    demandScan: async function(contextGroupID: number, 
        eLynxObject: IeLynxObject, 
        signal?: AbortSignal, 
        writeOnly?: boolean, 
        email?: string): Promise<string[]> {

        const queryParms: DemandScanQueryParms = {
            emailAddress: email,
            writeOnly: writeOnly
        };
    
        const path =`/groups/${contextGroupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/demandscan`;        
        const response = await restService.put<string, DemandScanQueryParms, string[]>(path, '', signal, queryParms);
        return response.data;
    },

    writeDown: async function(contextGroupID: number, 
        eLynxObject: IeLynxObject, 
        values: ITagValue[], 
        signal?: AbortSignal,         
        delayedWrite?: boolean, 
        email?: string): Promise<string[]> {

        const queryParms: WriteDownQueryParams = {
            emailAddress: email,
            delayedWrite: delayedWrite
        };
    
        const path =`/groups/${contextGroupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/writedown`;        
        const response = await restService.put<ITagValue[], WriteDownQueryParams, string[]>(path, values, signal, queryParms);
        return response.data;
    },
    getDynacard: async function(contextGroupID: number, deviceID: number, dynacardType?: DynaCardTypeEnum, signal?: AbortSignal): Promise<void> {

        const path =`/groups/${contextGroupID}/devices/${deviceID}/dynacards` + (dynacardType ? `/${dynacardType}` : '');
        await restService.get(path, signal);
    },
    getLiveStroke: async function(contextGroupID: number, deviceID: number, signal?: AbortSignal): Promise<string[]> {

        const path =`/groups/${contextGroupID}/devices/${deviceID}/livestrokes`;
        const response = await restService.get<string[]>(path, signal);
        return response.data;
    },
    getPollingStatistics: async function(contextGroupID: number, objectID: number, pollingServer: string, acmObjectName: string, signal?: AbortSignal): Promise<string[]> {

        const path = `/groups/${contextGroupID}/pollingstatistics`;

        const queryParams: PollingStatisticsQueryParams = {
            objectID,
            pollingServer,
            acmObjectName
        }

        const response = await restService.get<string[], PollingStatisticsQueryParams>(path, signal, queryParams);
        return response.data;
    },
    getObjectStatus: async function(contextGroupID: number, 
        eLynxObject: IeLynxObject, 
        statusIDList: string[],
        signal?: AbortSignal): Promise<ILiveDataStatusModel[]> {

        const path =`/groups/${contextGroupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/livedatastatus`;        
        const response = await restService.put<string[], ILiveDataStatusModel[]>(path, statusIDList, signal);
        return response.data;
    },
    getStatus: async function(contextGroupID: number, statusID: string, signal?: AbortSignal): Promise<ILiveDataStatusModel | undefined> {

        const queryParams: StatusQueryParams = {
            contextGroupID: contextGroupID
        }

        const path =`/livedatastatus/${statusID}`;
        const response = await restService.get<ILiveDataStatusModel | undefined, StatusQueryParams>(path, signal, queryParams);
        return response.data;
    },
    reportError: async function(contextGroupID: number, statusID: string, signal?: AbortSignal): Promise<void> {

        // var path: string = [
        //     "livedatastatus",
        //     statusID,
        //     "reporterror"
        // ].join("/");

        // var parms: Models.IRestQueryParams = <Models.IRestQueryParams>{
        //     contextGroupID: contextGroupID
        // };

        // return this._restService.put(path, "", parms).then((data: Services.IRestResponse<any>) => {
        //     return data.entity;
        // });

        const queryParams: StatusQueryParams = {
            contextGroupID: contextGroupID
        };

        const path =`/livedatastatus/${statusID}/reporterror`;
        await restService.put<string, StatusQueryParams, string>(path, '', signal, queryParams);
    },
    testTagRegister: async function(contextGroupID: number, registerInfo: ITagRegisterInfo): Promise<string[]> {
        const path =`/groups/${contextGroupID}/register/livedatastatus`;
        const response = await restService.post<ITagRegisterInfo, string[]>(path, registerInfo);
        return response.data;
    }
};

export default LiveDataService;